// src/pages/CRM/ActivityComm/TaskList.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, MenuItem, Select,
  FormControl, InputLabel, CircularProgress, Alert, Tooltip, Card, CardContent,
  Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  Assignment as TaskIcon,
  Warning as WarningIcon,
  Today as TodayIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { taskService } from './activityCommService';
import {
  TaskStatusLabels, TaskStatusColors, TaskStatusOptions,
  TaskPriorityLabels, TaskPriorityColors, TaskPriorityOptions,
  formatDateTime, formatTimeAgo, isOverdue
} from './activityCommEnums';
import TaskForm from './TaskForm';

const TaskList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    overdue: 0,
    due_today: 0,
    completed: 0
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });

  // Dialog states
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === 'true');
  const [selectedTask, setSelectedTask] = useState(null);

  // Tab for view filter
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    document.title = 'Tasks | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadTasks();
      loadStats();
    }
  }, [canView, chatbotId, pagination.page, pagination.per_page, searchTerm, filters, tabValue]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = {};
      if (chatbotId) params.chatbot_id = chatbotId;
      const response = await taskService.getStats(params);
      if (response.success) {
        setStats({
          total: response.data?.total || 0,
          overdue: response.data?.overdue || 0,
          due_today: response.data?.due_today || 0,
          completed: response.data?.completed || 0
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        per_page: pagination.per_page,
        search: searchTerm || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        sort_by: 'due_date',
        sort_order: 'asc'
      };

      if (chatbotId) params.chatbot_id = chatbotId;

      // Tab-specific params
      if (tabValue === 1) {
        params.assigned_to = 'me';
      } else if (tabValue === 2) {
        params.is_overdue = true;
      }

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await taskService.getAll(params);
      if (response.success) {
        setTasks(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 0
        }));
      }
    } catch (err) {
      enqueueSnackbar('Failed to load tasks', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '' });
    setSearchTerm('');
    setTabValue(0);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({ ...prev, per_page: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleFormClose = (refresh = false) => {
    setFormOpen(false);
    setSelectedTask(null);
    searchParams.delete('new');
    setSearchParams(searchParams);
    if (refresh) {
      loadTasks();
      loadStats();
    }
  };

  const handleFormSuccess = () => {
    handleFormClose(true);
  };

  const formatDueDate = (dueDate, status) => {
    if (!dueDate) return '-';
    const overdue = isOverdue(dueDate, status);
    return (
      <Typography
        variant="body2"
        sx={{ color: overdue ? 'error.main' : 'text.primary', fontWeight: overdue ? 600 : 400 }}
      >
        {formatDateTime(dueDate)}
      </Typography>
    );
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view tasks.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pagination.total} total tasks
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => { loadTasks(); loadStats(); }} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
            >
              New Task
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <TaskIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <WarningIcon color="error" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700} color="error.main">{stats.overdue}</Typography>
              <Typography variant="body2" color="text.secondary">Overdue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <TodayIcon color="warning" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700} color="warning.main">{stats.due_today}</Typography>
              <Typography variant="body2" color="text.secondary">Due Today</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={700} color="success.main">{stats.completed}</Typography>
              <Typography variant="body2" color="text.secondary">Completed</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Quick Filters */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="All Tasks" />
          <Tab label="My Tasks" />
          <Tab label="Overdue" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tasks by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TaskStatusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                label="Priority"
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TaskPriorityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tasks Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tasks found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => (
                  <TableRow
                    key={task.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/tasks/${task.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontWeight: 500 }}>
                        {task.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TaskPriorityLabels[task.priority] || task.priority}
                        color={TaskPriorityColors[task.priority] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TaskStatusLabels[task.status] || task.status}
                        color={TaskStatusColors[task.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDueDate(task.due_date, task.status)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {task.assigned_user_name || 'Unassigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {task.contact?.full_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(task.created_at)}>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimeAgo(task.created_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Task Form Dialog */}
      <TaskForm
        open={formOpen}
        onClose={() => handleFormClose(false)}
        task={selectedTask}
        onSuccess={handleFormSuccess}
      />
    </Box>
  );
};

export default TaskList;
